import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { NotificationManager } from '@/components/Notification'
import { WishlistService } from '@/services'
import { useAppSelector } from '@/store/hook'
import { selectUserId } from '@/store/slices/authenticationSlice'
import { useDebounce } from '@/hooks'
import { linkGenerator } from '@/libs/utils/language'

import type { WishlistApiResponse } from './types'

export const WISHLIST_OVERVIEW_PAGE_SIZE = 6

interface UseFetchWishlistsReturn {
  fetchOwnWishlists: {
    data?: WishlistApiResponse
    isFetching: boolean
    error: Error | null
  }
  fetchSharedWishlists: {
    data?: WishlistApiResponse
    isFetching: boolean
    error: Error | null
  }
  searchQuery: string
  ownPageNumber: number
  sharedPageNumber: number
  handleChangeText: (text: string) => void
  handleChangeOwnPage: (newPage: number) => void
  handleChangeSharedPage: (newPage: number) => void
}

export const useFetchWishlists = (): UseFetchWishlistsReturn => {
  const ownerId = useAppSelector(selectUserId)
  const [query, setQuery] = useState<string>('')
  const [ownPageNumber, setOwnPageNumber] = useState<number>(1)
  const [sharedPageNumber, setSharedPageNumber] = useState<number>(1)
  const searchQuery = useDebounce(query, 300)
  const pageSize = WISHLIST_OVERVIEW_PAGE_SIZE

  // Refs to save page numbers before searching
  const savedPageNumbersRef = useRef<{ ownPage: number; sharedPage: number } | null>(null)
  const previousSearchQueryRef = useRef<string>('')

  const handleChangeText = useCallback((text: string) => {
    setQuery(text)
  }, [])

  const handleChangeOwnPage = useCallback((newPage: number) => {
    setOwnPageNumber(newPage)
  }, [])

  const handleChangeSharedPage = useCallback((newPage: number) => {
    setSharedPageNumber(newPage)
  }, [])

  useEffect(() => {
    const wasSearching = previousSearchQueryRef.current !== ''
    const isSearching = searchQuery !== ''

    if (!wasSearching && isSearching) {
      // Transitioning from no search to search: save current pages and reset to 1
      savedPageNumbersRef.current = { ownPage: ownPageNumber, sharedPage: sharedPageNumber }
      setOwnPageNumber(1)
      setSharedPageNumber(1)
    } else if (wasSearching && !isSearching) {
      // Transitioning from search to no search: restore saved pages
      if (savedPageNumbersRef.current) {
        setOwnPageNumber(savedPageNumbersRef.current.ownPage)
        setSharedPageNumber(savedPageNumbersRef.current.sharedPage)
        savedPageNumbersRef.current = null
      }
    }

    previousSearchQueryRef.current = searchQuery
  }, [searchQuery, ownPageNumber, sharedPageNumber])

  const fetchOwnWishlists = useQuery({
    queryKey: ['wishlists', 'owner', ownPageNumber, pageSize, searchQuery],
    queryFn: async (): Promise<WishlistApiResponse> => {
      const { data } = await WishlistService.getWishlistOverview({
        ownerId,
        searchQuery,
        pageNumber: ownPageNumber,
        pageSize,
        type: 'owner',
      })
      return data as WishlistApiResponse
    },
    enabled: Boolean(ownerId),
    placeholderData: keepPreviousData,
  })

  const fetchSharedWishlists = useQuery({
    queryKey: ['wishlists', 'shared', sharedPageNumber, pageSize, searchQuery],
    queryFn: async (): Promise<WishlistApiResponse> => {
      const { data } = await WishlistService.getWishlistOverview({
        ownerId,
        searchQuery,
        pageNumber: sharedPageNumber,
        pageSize,
        type: 'shared',
      })
      return data as WishlistApiResponse
    },
    enabled: Boolean(ownerId),
    placeholderData: keepPreviousData,
  })

  return {
    fetchOwnWishlists,
    fetchSharedWishlists,
    searchQuery,
    ownPageNumber,
    sharedPageNumber,
    handleChangeText,
    handleChangeOwnPage,
    handleChangeSharedPage,
  }
}

interface DeleteWishlistParams {
  wishlistId: string
  wishlistOwnerId: string
}

interface UseDeleteWishlistOptions {
  shouldNavigate?: boolean
}

export const useDeleteWishlist = (options: UseDeleteWishlistOptions = {}) => {
  const { shouldNavigate = false } = options
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async ({ wishlistId, wishlistOwnerId }: DeleteWishlistParams) => {
      await WishlistService.deleteWishlist({ wishlistId, wishlistOwnerId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['wishlists', 'owner'],
      })

      NotificationManager.success({
        message: 'notification.success.deleteWishlist',
      })

      if (shouldNavigate) {
        navigate(linkGenerator('/wishlists'))
      }
    },
    onError: (error) => {
      NotificationManager.error({
        message: 'notification.error.deleteWishlist',
        description: error,
      })
    },
  })
}

interface RenameWishlistParams {
  wishlistId: string
  ownerId: string
  newName: string
}

export const useRenameWishlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ wishlistId, ownerId, newName }: RenameWishlistParams) => {
      const response = await WishlistService.renameWishlist({
        wishlistId,
        ownerId,
        newName,
      })
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['wishlists', 'owner'],
      })

      NotificationManager.success({
        message: 'notification.success.editWishlist',
      })
    },
    onError: (error) => {
      NotificationManager.error({
        message: 'notification.error.editWishlist',
        description: error,
      })
    },
  })
}

interface AddProductToWishlistParams {
  ownerId: string
  wishlistId: string
  materialNumber: string
  materialName?: string
  quantity: number
}

export const useAddProductToWishlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      { ownerId, wishlistId, materialNumber, materialName, quantity }: AddProductToWishlistParams,
    ) => {
      const response = await WishlistService.addProductToWishlist({
        ownerId,
        wishlistId,
        materialNumber,
        materialName,
        quantity,
      })
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['wishlists', 'owner'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['wishlists', 'detail'],
      })
    },
  })
}

interface CreateWishlistParams {
  wishlistName: string
}

export const useCreateWishlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ wishlistName }: CreateWishlistParams) => {
      const response = await WishlistService.createWishlist({
        newWishlistName: wishlistName,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['wishlists', 'owner'],
      })
    },
  })
}

interface CreateAndAddProductToWishlistParams {
  wishlistName: string
  ownerId: string
  materialNumber: string
  materialName?: string
  quantity: number
}

export const useCreateAndAddProductToWishlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      wishlistName,
      ownerId,
      materialNumber,
      materialName,
      quantity,
    }: CreateAndAddProductToWishlistParams) => {
      const createResponse = await WishlistService.createWishlist({
        newWishlistName: wishlistName,
      })
      const wishlistId = createResponse.data

      const addResponse = await WishlistService.addProductToWishlist({
        ownerId,
        wishlistId,
        materialNumber,
        materialName,
        quantity,
      })

      return addResponse.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['wishlists', 'owner'],
      })
    },
  })
}

interface RemoveProductFromWishlistParams {
  wishlistOwnerId: string
  wishlistId: string
  materialNumber: string
}

export const useRemoveProductFromWishlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      { wishlistOwnerId, wishlistId, materialNumber }: RemoveProductFromWishlistParams,
    ) => {
      const response = await WishlistService.removeProductInWishlist({
        wishlistOwnerId,
        wishlistId,
        materialNumber,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['wishlists', 'owner'],
      })
      queryClient.invalidateQueries({
        queryKey: ['wishlists', 'detail'],
      })
    },
  })
}

interface RelocateWishlistItemParams {
  ownerId: string
  wishlistId: string
  order: number
}

export const useRelocateWishlists = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (items: RelocateWishlistItemParams[]) => {
      await WishlistService.relocateWishlists(items)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['wishlists', 'owner'] })
    },
    onError: () => {
      NotificationManager.error({
        message: 'notification.error.relocateWishlists',
      })
    },
  })
}
